import React, { useRef, useEffect } from 'react';
import { useInView, useAnimation } from 'framer-motion';
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
} from 'recharts';
import { formatCurrency, formatCompactNumber } from '../../utils/formatters';
import {
  StyledChartContainer,
  StyledChartHeader,
  StyledChartTitle,
  StyledChartInfo,
  StyledMetric,
  StyledMetricLabel,
  StyledMetricValue,
  StyledTooltip,
} from './AdvancedCharts.styles';

// Gradient Definitions
const gradients = {
  income: 'url(#incomeGradient)',
  expense: 'url(#expenseGradient)',
  balance: 'url(#balanceGradient)',
  primary: 'url(#primaryGradient)',
};

const GradientDefs = () => (
  <defs>
    <linearGradient id='incomeGradient' x1='0' y1='0' x2='0' y2='1'>
      <stop offset='0%' stopColor='#10b981' stopOpacity={0.8} />
      <stop offset='100%' stopColor='#10b981' stopOpacity={0.1} />
    </linearGradient>
    <linearGradient id='expenseGradient' x1='0' y1='0' x2='0' y2='1'>
      <stop offset='0%' stopColor='#ef4444' stopOpacity={0.8} />
      <stop offset='100%' stopColor='#ef4444' stopOpacity={0.1} />
    </linearGradient>
    <linearGradient id='balanceGradient' x1='0' y1='0' x2='0' y2='1'>
      <stop offset='0%' stopColor='#6366f1' stopOpacity={0.8} />
      <stop offset='100%' stopColor='#6366f1' stopOpacity={0.1} />
    </linearGradient>
    <linearGradient id='primaryGradient' x1='0' y1='0' x2='0' y2='1'>
      <stop offset='0%' stopColor='#8b5cf6' stopOpacity={0.8} />
      <stop offset='100%' stopColor='#8b5cf6' stopOpacity={0.1} />
    </linearGradient>
  </defs>
);

// Custom Tooltip Component
const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{
    color: string;
    value: number | string;
    name?: string;
    dataKey?: string;
  }>;
  label?: string;
}) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <StyledTooltip>
      <div className='tooltip-label'>{label}</div>
      {payload.map((item, index: number) => (
        <div key={index} className='tooltip-item'>
          <div className='tooltip-color' style={{ backgroundColor: item.color }} />
          <span className='tooltip-value'>
            {typeof item.value === 'number' ? formatCurrency(item.value) : item.value}
          </span>
          <span className='tooltip-name'>{item.name || item.dataKey}</span>
        </div>
      ))}
    </StyledTooltip>
  );
};

// Utility function for compact formatting
const formatCompactCurrency = (value: number): string => {
  if (Math.abs(value) >= 1000000) {
    return formatCompactNumber(value);
  }

  return formatCurrency(value);
};

// Trend Line Chart with Advanced Effects
interface TrendChartProps {
  data: Array<{ month: string; income: number; expenses: number; balance?: number }>;
  title: string;
  height?: number;
}

export const TrendChart: React.FC<TrendChartProps> = ({ data, title, height = 300 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref);
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start({
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: 'easeOut' },
      });
    }
  }, [isInView, controls]);

  const totalIncome = data.reduce((sum, item) => sum + item.income, 0);
  const avgIncome = totalIncome / data.length;

  return (
    <StyledChartContainer ref={ref} initial={{ opacity: 0, y: 50 }} animate={controls}>
      <StyledChartHeader>
        <StyledChartTitle>{title}</StyledChartTitle>
        <StyledChartInfo>
          <StyledMetric>
            <StyledMetricLabel>Avg Income</StyledMetricLabel>
            <StyledMetricValue>{formatCompactCurrency(avgIncome)}</StyledMetricValue>
          </StyledMetric>
          <StyledMetric>
            <StyledMetricLabel>Total</StyledMetricLabel>
            <StyledMetricValue>{formatCompactCurrency(totalIncome)}</StyledMetricValue>
          </StyledMetric>
        </StyledChartInfo>
      </StyledChartHeader>

      <ResponsiveContainer width='100%' height={height}>
        <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <GradientDefs />
          <CartesianGrid strokeDasharray='3 3' stroke='rgba(0,0,0,0.1)' horizontal={true} vertical={false} />
          <XAxis dataKey='month' axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} dy={10} />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#6b7280' }}
            tickFormatter={formatCompactCurrency}
            width={60}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type='monotone'
            dataKey='income'
            stroke='#10b981'
            strokeWidth={3}
            fill={gradients.income}
            fillOpacity={0.6}
            name='Income'
          />
          <Area
            type='monotone'
            dataKey='expenses'
            stroke='#ef4444'
            strokeWidth={3}
            fill={gradients.expense}
            fillOpacity={0.6}
            name='Expenses'
          />
        </AreaChart>
      </ResponsiveContainer>
    </StyledChartContainer>
  );
};

// Enhanced Bar Chart
interface BarChartProps {
  data: Array<{ month: string; income: number; expenses: number }>;
  title: string;
  height?: number;
}

export const EnhancedBarChart: React.FC<BarChartProps> = ({ data, title, height = 300 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref);
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start({
        opacity: 1,
        scale: 1,
        transition: { duration: 0.8, ease: 'easeOut' },
      });
    }
  }, [isInView, controls]);

  return (
    <StyledChartContainer ref={ref} initial={{ opacity: 0, scale: 0.9 }} animate={controls}>
      <StyledChartHeader>
        <StyledChartTitle>{title}</StyledChartTitle>
      </StyledChartHeader>

      <ResponsiveContainer width='100%' height={height}>
        <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray='3 3' stroke='rgba(0,0,0,0.1)' horizontal={true} vertical={false} />
          <XAxis dataKey='month' axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#6b7280' }}
            tickFormatter={formatCompactCurrency}
            width={60}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey='income' fill='#10b981' name='Income' radius={[4, 4, 0, 0]} opacity={0.9} />
          <Bar dataKey='expenses' fill='#ef4444' name='Expenses' radius={[4, 4, 0, 0]} opacity={0.9} />
        </BarChart>
      </ResponsiveContainer>
    </StyledChartContainer>
  );
};

// Advanced Pie Chart with Animations
interface PieChartProps {
  data: Array<{ category: string; amount: number; percentage: number }>;
  title: string;
  height?: number;
}

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'];

export const EnhancedPieChart: React.FC<PieChartProps> = ({ data, title, height = 300 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref);
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start({
        opacity: 1,
        rotate: 0,
        transition: { duration: 1, ease: 'easeOut' },
      });
    }
  }, [isInView, controls]);

  const total = data.reduce((sum, item) => sum + item.amount, 0);

  return (
    <StyledChartContainer ref={ref} initial={{ opacity: 0, rotate: -180 }} animate={controls}>
      <StyledChartHeader>
        <StyledChartTitle>{title}</StyledChartTitle>
        <StyledChartInfo>
          <StyledMetric>
            <StyledMetricLabel>Total</StyledMetricLabel>
            <StyledMetricValue>{formatCompactCurrency(total)}</StyledMetricValue>
          </StyledMetric>
        </StyledChartInfo>
      </StyledChartHeader>

      <ResponsiveContainer width='100%' height={height}>
        <PieChart>
          <Pie
            data={data}
            cx='50%'
            cy='50%'
            labelLine={false}
            label={({ percentage }) => `${percentage.toFixed(0)}%`}
            outerRadius={Math.min(height * 0.35, 120)}
            innerRadius={Math.min(height * 0.2, 60)}
            fill='#8884d8'
            dataKey='amount'
            stroke='none'
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      </ResponsiveContainer>
    </StyledChartContainer>
  );
};
